import { Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ButtonDefault from '../components/base/ButtonDefault';
import PageHeader from '../components/base/PageHeader';
import { BodyPanel } from '../components/base/Panel';
import DocumentEditor from '../components/cms/DocumentEditor';
import PathGenerators from '../constants/pathGenerators';
import AuthorizedPage from '../containers/AuthorizedPage';
import cmsService from '../services/cmsService';
import { DocumentDataField } from '../services/transportTypes/NCms';
import { validateFieldValue } from '../utilities/DocumentUtils';
import useAlert from '../utilities/hooks/useAlert';
import useDocumentSchema from '../utilities/hooks/useDocumentSchema';
import useIdSearch from '../utilities/hooks/useIdSearch';
import useProject from '../utilities/hooks/useProject';

const DocumentCreate = (): JSX.Element => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const alert = useAlert();

  const schemaId = useIdSearch();
  const [project, projectLoading] = useProject();
  const [schema, schemaLoading] = useDocumentSchema(project, schemaId);

  const [name, setName] = useState<string>('');
  const [sequence, setSequence] = useState<number>(0);

  const [data, setData] = useState<Record<string, DocumentDataField> | null>(
    null,
  );
  const [errors, setErrors] = useState<string[]>([]);

  const createDocumentMutation = useMutation({
    mutationKey: [cmsService.addDocument.name],
    mutationFn: cmsService.addDocument,
    onSuccess(response) {
      alert({
        message: `${response.name} created.`,
        severity: 'success',
      });
      navigate(
        PathGenerators.DOCUMENT_EDIT(project?.slug, response.id.toString()),
      );
      queryClient.setQueriesData(
        [
          cmsService.getDocument.name,
          { projectId: project?.slug, documentId: response.id },
        ],
        data,
      );
    },
  });

  const handleDataChange = (fieldName: string, value: DocumentDataField) => {
    // console.log('handleDataChange', {
    //   fieldName,
    //   value,
    //   previousData: data,
    // });

    setData((previous) => ({
      ...previous,
      [fieldName]: value,
    }));
  };

  const handleSave = () => {
    const saveErrors: string[] = [];
    if (!name) {
      saveErrors.push('name');
    }

    if (!sequence || sequence < 0) {
      saveErrors.push('sequence');
    }

    if (!data) {
      saveErrors.push('data');
    } else {
      schema?.fields.forEach((f) => {
        if (!validateFieldValue(data[f.name], f)) {
          saveErrors.push(f.name);
        }
      });
    }

    if (!saveErrors.length) {
      // console.log('Save', { name, sequence, data });
      createDocumentMutation.mutate({
        projectId: project!.id,
        body: {
          schema_id: schemaId!,
          name,
          sequence,
          data: data!,
        },
      });
    }

    setErrors(saveErrors);
  };

  useEffect(() => {
    if (schema && !data) {
      console.log('Resetting data fields');
      setData(
        schema.fields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: null,
          }),
          {},
        ),
      );
    }
  }, [schema]);

  return (
    <AuthorizedPage>
      <PageHeader
        title={`${schema?.name ?? 'CMS'} - Create Document`}
        hintMessage="Create a new document."
        displaySearch
        displayGoBack
      />
      <BodyPanel>
        <Stack spacing={2}>
          <DocumentEditor
            name={name}
            sequence={sequence}
            fields={schema?.fields ?? []}
            data={data}
            errors={errors}
            isLoading={projectLoading || schemaLoading || !data}
            onNameChange={setName}
            onSequenceChange={setSequence}
            onDataChange={handleDataChange}
          />
          <Stack direction="row" justifyContent="flex-end">
            <ButtonDefault
              onClick={handleSave}
              disabled={
                projectLoading ||
                schemaLoading ||
                !data ||
                createDocumentMutation.isSuccess
              }
            >
              Save
            </ButtonDefault>
          </Stack>
        </Stack>
      </BodyPanel>
    </AuthorizedPage>
  );
};

export default DocumentCreate;
